import { useQuery } from "@tanstack/react-query";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import type { GridRow } from "./api";
import { mergeIssues } from "./feed_issues";
import { FeedIssuesNote } from "./FeedIssuesNote";
import { money, pct1 } from "./format";
import { monthGapText, monthLabel, SKU_MONTHS_COPY as T, skuMonthsQuery } from "./sku_months";

const COUNT = new Intl.NumberFormat("en-GB");

/** The recent Shopify actuals C2 serves for a row. All economics remain C2-owned; this component only formats its strings. */
export function SkuMonthsSection({ row }: { row: GridRow }) {
  const query = useQuery(skuMonthsQuery(row.xero_sku));
  const s = query.data;
  return (
    <section aria-label={T.title}>
      <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-ks-purple">{T.title}</h3>
      <p className="text-xs text-ks-grey-1">{T.intro}</p>
      {query.isError ? <p role="status">{T.unavailable}</p> : !s ? (
        <p role="status">Working out the last 3 months</p>
      ) : (
        <>
          {/* C1 v1.64 (C2 v1.62): the order feeds' open issues, in the drawer's compact form. */}
          <FeedIssuesNote issues={mergeIssues(s.feed_issues)} compact />
          <Table>
            <TableHeader><TableRow>{T.head.map((head) => <TableHead key={head}>{head}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {s.months.map((month) => (
                <TableRow key={month.month} data-month={month.month}>
                  <TableCell>{monthLabel(month.month)}</TableCell>
                  <TableCell className="tabular-nums">{COUNT.format(month.units)}</TableCell>
                  <TableCell className="tabular-nums">{money(month.revenue, s.currency)}</TableCell>
                  <TableCell className="tabular-nums">{money(month.profit, s.currency)}</TableCell>
                  <TableCell className="tabular-nums">{money(month.profit_per_unit, s.currency)}</TableCell>
                  <TableCell className="tabular-nums">{pct1(month.margin_pct)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {s.months.filter((month) => month.missing.length > 0).map((month) => (
            <p key={month.month} className="text-xs text-ks-grey-1">{monthLabel(month.month)}: {month.missing.map(monthGapText).join(", ")}</p>
          ))}
          <p className="text-xs text-ks-grey-1">{T.coverage(s.months.map((month) => T.coveragePart(monthLabel(month.month), month.orders, month.orders_total)))}</p>
          <p className="text-xs text-ks-grey-1">{s.basis}</p>
        </>
      )}
    </section>
  );
}
